import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Sorting.module.css';
import { generateUnsortedArray } from '../../lib/arrayGenerator';

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

const QUICK_SORT_CODE = `function quickSort(arr, low, high) {
  if (low < high) {
    let pi = partition(arr, low, high);
    quickSort(arr, low, pi - 1);
    quickSort(arr, pi + 1, high);
  }
}

function partition(arr, low, high) {
  let pivot = arr[high];
  let i = (low - 1);

  for (let j = low; j < high; j++) {
    if (arr[j] < pivot) {
      i++;
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }
  [arr[i + 1], arr[high]] = [arr[high], arr[i + 1]];
  return (i + 1);
}`;

const QuickSort: React.FC = () => {
  const navigate = useNavigate();
  const [array, setArray] = useState<number[]>([]);
  const [isSorting, setIsSorting] = useState(false);
  const [pivotIndex, setPivotIndex] = useState<number | null>(null);
  const [leftPointer, setLeftPointer] = useState<number | null>(null);
  const [rightPointer, setRightPointer] = useState<number | null>(null);
  const [sortedIndices, setSortedIndices] = useState<number[]>([]);
  const [highlightedLine, setHighlightedLine] = useState<number | null>(null);
  const stopSort = useRef(false);

  const handleStop = useCallback(() => {
    stopSort.current = true;
    setIsSorting(false);
  }, []);

  const handleRandomize = useCallback(() => {
    handleStop();
    setPivotIndex(null);
    setLeftPointer(null);
    setRightPointer(null);
    setSortedIndices([]);
    setHighlightedLine(null);
    setArray(generateUnsortedArray(15, 100));
  }, [handleStop]);

  useEffect(() => {
    handleRandomize();
    return () => {
      stopSort.current = true;
    };
  }, [handleRandomize]);

  const handleGoBack = () => navigate(-1);

  const partition = async (arr: number[], low: number, high: number): Promise<number> => {
    setHighlightedLine(10);
    let pivot = arr[high];
    setPivotIndex(high);
    await delay(300);

    setHighlightedLine(11);
    let i = low - 1;
    
    for (let j = low; j < high; j++) {
      if (stopSort.current) return -1;
      setHighlightedLine(13);
      setLeftPointer(i + 1);
      setRightPointer(j);
      await delay(300);

      if (arr[j] < pivot) {
        setHighlightedLine(14);
        await delay(300);
        i++;
        setLeftPointer(i);
        setHighlightedLine(16);
        [arr[i], arr[j]] = [arr[j], arr[i]];
        setArray([...arr]);
        await delay(300);
      }
    }

    if (stopSort.current) return -1;
    setHighlightedLine(19);
    [arr[i + 1], arr[high]] = [arr[high], arr[i + 1]];
    setArray([...arr]);
    await delay(300);

    setHighlightedLine(20);
    return i + 1;
  };

  const quickSortHelper = async (arr: number[], low: number, high: number) => {
    if (stopSort.current) return;
    setHighlightedLine(2);
    await delay(300);
    if (low < high) {
      let pi = await partition(arr, low, high);
      if (pi === -1) return;

      setSortedIndices(prev => [...prev, pi]);
      setPivotIndex(null);
      setLeftPointer(null);
      setRightPointer(null);
      
      setHighlightedLine(4);
      await quickSortHelper(arr, low, pi - 1);
      if (stopSort.current) return;
      await delay(300);

      setHighlightedLine(5);
      await quickSortHelper(arr, pi + 1, high);
      if (stopSort.current) return;
      await delay(300);
    } else {
      if (low >= 0 && low < arr.length) {
        setSortedIndices(prev => [...prev, low]);
      }
    }
  };

  const runQuickSort = async () => {
    stopSort.current = false;
    setPivotIndex(null);
    setLeftPointer(null);
    setRightPointer(null);
    setSortedIndices([]);
    setHighlightedLine(null);
    setIsSorting(true);

    let arrCopy = [...array];
    await quickSortHelper(arrCopy, 0, arrCopy.length - 1);

    if (!stopSort.current) {
      setSortedIndices(Array.from(Array(arrCopy.length).keys()));
    }
    setHighlightedLine(null);
    setIsSorting(false);
  };

  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>      
      <h1 className={styles.title}>Quick Sort</h1>
        <div className={styles.description}>
          <h2>Description</h2>
          <p>Quick Sort is a highly efficient sorting algorithm that uses a "divide and conquer" strategy. It picks an element as a pivot and partitions the other elements around it.</p>
        </div>
        <div className={styles.useCase}>
          <h3>When to use it?</h3>
          <ul>
            <li>For large, unsorted datasets where in-place sorting is important (it uses minimal extra memory).</li>
            <li>When average-case performance is more critical than worst-case performance.</li>
          </ul>
        </div>
        <div className={styles.timeComplexity}>
          <h3>Time Complexity</h3>
          <p>
            <b>Best/Average Case:</b> O(n log n)
            <br />
            <b>Worst Case:</b> O(n²)
          </p>
        </div>
      
      <div className={styles.visualization}>
        {array.map((value, index) => (
          <div
            key={index}
            className={`
              ${styles.bar}
              ${pivotIndex === index ? styles.pivot : ''}
              ${leftPointer === index ? styles.leftPointer : ''}
              ${rightPointer === index ? styles.rightPointer : ''}
              ${sortedIndices.includes(index) ? styles.sorted : ''}
            `}
            style={{ height: `${value * 3.5}px` }}
          >
            <span className={styles.barValue}>{value}</span>
          </div>
        ))}
      </div>

      <div className={styles.controls}>
        <button onClick={runQuickSort} disabled={isSorting}>Run Quick Sort</button>
        <button onClick={handleRandomize} disabled={isSorting}>Randomize</button>
        <button onClick={handleStop} disabled={!isSorting}>Stop</button>
      </div>

      <div className={styles.codeDisplay}>
        {QUICK_SORT_CODE.split('\n').map((line, index) => (
          <div key={index} className={`${styles.codeLine} ${highlightedLine === index + 1 ? styles.highlightedLine : ''}`}>
            {line}
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuickSort;